import { useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      toast(err?.response?.data?.detail || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen grid md:grid-cols-2"
      data-testid="page-forgot-password"
    >
      {/* Brand panel */}
      <div className="hidden md:flex flex-col justify-between p-10"
        style={{ background: "linear-gradient(155deg, #0a5030 0%, #0e6939 55%, #14824a 100%)" }}>
        <div className="text-white/85">
          <div className="text-sm font-bold tracking-tight">Jero-One</div>
          <div className="text-xs uppercase tracking-widest text-white/60">Workspace</div>
        </div>
        <div className="max-w-md space-y-4 text-white">
          <h1 className="text-4xl font-bold tracking-tight leading-tight">
            Forgot your password?
          </h1>
          <p className="text-white/75 text-[15px] leading-relaxed">
            Enter your email and we&apos;ll send you a one-time link (valid for
            60 minutes) to choose a new password. If you don&apos;t receive it
            within a few minutes, contact support@jero-onetechnologies.com.
          </p>
        </div>
        <div className="text-xs text-white/55">
          © {new Date().getFullYear()} Jero-One Technologies Ltd. · Uganda | USA | Canada
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-xs muted hover:text-[#0e6939]"
            data-testid="back-to-login"
          >
            <ArrowLeft size={12} /> Back to sign in
          </Link>

          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <KeyRound size={20} /> Password reset
            </h2>
            <p className="text-sm muted mt-1">
              We&apos;ll email you a secure link to reset it.
            </p>
          </div>

          {sent ? (
            <div
              className="rounded-lg border border-[#0e6939] bg-[#f3faf4] p-4 text-sm"
              data-testid="reset-sent"
            >
              <div className="font-semibold" style={{ color: "var(--green-700)" }}>
                Check your inbox
              </div>
              <p className="text-xs mt-1 muted">
                If <b>{email}</b> is registered with us, a reset link is on its
                way. The link is valid for 60 minutes. Remember to check spam.
              </p>
              <Link
                to="/login"
                className="btn btn-primary btn-sm mt-4"
                data-testid="reset-sent-back"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="label">Email address</label>
                <input
                  className="input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="forgot-email"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={loading || !email}
                data-testid="forgot-submit"
              >
                {loading ? "Sending…" : "Email me a reset link"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
